import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Link,
} from '@mui/material';
import {
  Feedback as FeedbackIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  VideoCall as VideoCallIcon,
} from '@mui/icons-material';
import { useToast } from '../../contexts/ToastContext';
import { getSpeakingSessions, updateSpeakingSession } from '../../api/adminApi';
import { useGroupedData } from '../../hooks';
import {
  PageHeader,
  StatsCards,
  GroupedAccordionList,
  ModernButton,
} from '../../components/common';
import { format } from 'date-fns';

const SpeakingFeedbackList = () => {
  const { showToast } = useToast();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedFeedback, setEditedFeedback] = useState({
    instructorFeedback: '',
    instructorAdvice: '',
  });

  // Use the grouped data hook - group by exam
  const {
    groupsArray,
    isGroupExpanded,
    toggleGroup,
  } = useGroupedData({
    data: sessions,
    getGroupKey: (session) => session.examId?._id || session.userProductRegistrationId?.productId?._id || 'unknown',
    getGroupMeta: (session) => ({
      examName: session.examId?.name || session.examId?.label || session.userProductRegistrationId?.productId?.name || session.userProductRegistrationId?.productId?.label || 'Unknown Exam',
      examDate: session.examId?.startsAt || session.userProductRegistrationId?.productId?.startDate,
    }),
    itemsKey: 'sessions',
  });

  // Fetch data - only completed sessions
  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getSpeakingSessions({ isCompleted: true });
      const sessionsData = data?.data?.sessions || data?.sessions || [];
      setSessions(sessionsData);
    } catch (error) {
      console.error('Failed to fetch speaking sessions:', error);
      showToast('Failed to load speaking sessions', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Helper functions
  const getStudentName = (session) => {
    const user = session.userProductRegistrationId?.userId;
    if (user?.firstName || user?.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }
    if (user?.name) return user.name;
    return 'N/A';
  };

  const getStudentEmail = (session) => {
    return session.userProductRegistrationId?.userId?.email || 'N/A';
  };

  const getInstructorName = (session) => {
    const instructor = session.instructorId;
    if (instructor?.firstName || instructor?.lastName) {
      return `${instructor.firstName || ''} ${instructor.lastName || ''}`.trim();
    }
    return 'N/A';
  };

  const hasFeedback = (session) => {
    return (session.instructorFeedback && session.instructorFeedback.trim()) ||
           (session.instructorAdvice && session.instructorAdvice.trim());
  };

  const getFeedbackStatus = (session) => {
    const hasFb = session.instructorFeedback && session.instructorFeedback.trim();
    const hasAdvice = session.instructorAdvice && session.instructorAdvice.trim();

    if (hasFb && hasAdvice) {
      return { status: 'complete', color: 'success', text: 'Complete' };
    } else if (hasFb || hasAdvice) {
      return { status: 'partial', color: 'warning', text: 'Partial' };
    } else {
      return { status: 'pending', color: 'error', text: 'Pending' };
    }
  };

  // Stats calculation
  const stats = [
    {
      label: 'Completed Sessions',
      value: sessions.length,
      color: 'primary.main',
    },
    {
      label: 'Feedback Complete',
      value: sessions.filter((s) => getFeedbackStatus(s).status === 'complete').length,
      color: 'success.main',
    },
    {
      label: 'Feedback Pending',
      value: sessions.filter((s) => getFeedbackStatus(s).status === 'pending').length,
      color: 'error.main',
    },
  ];

  // Dialog handlers
  const handleViewDetails = (session) => {
    setSelectedSession(session);
    setEditedFeedback({
      instructorFeedback: session.instructorFeedback || '',
      instructorAdvice: session.instructorAdvice || '',
    });
    setEditMode(false);
    setDialogOpen(true);
  };

  const handleEditFeedback = () => {
    setEditMode(true);
  };

  const handleSaveFeedback = async () => {
    try {
      await updateSpeakingSession(selectedSession._id, editedFeedback);
      showToast('Feedback updated successfully', 'success');
      setEditMode(false);
      fetchSessions();
      setDialogOpen(false);
    } catch (error) {
      console.error('Failed to update feedback:', error);
      showToast('Failed to update feedback', 'error');
    }
  };

  const handleFeedbackChange = (field, value) => {
    setEditedFeedback((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Render item for GroupedAccordionList
  const renderItem = (session) => {
    const feedbackStatus = getFeedbackStatus(session);

    return (
      <ListItem
        key={session._id}
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          py: 2.5,
          px: 3,
          backgroundColor: 'background.paper',
        }}
      >
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {getStudentName(session)}
              </Typography>
              <Chip
                label={`${session.sessionDuration || 15} min`}
                color="info"
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.75rem' }}
              />
              <Chip
                label={feedbackStatus.text}
                color={feedbackStatus.color}
                size="small"
                variant={feedbackStatus.status === 'complete' ? 'filled' : 'outlined'}
                sx={{ height: 20, fontSize: '0.75rem' }}
              />
            </Box>
          }
          secondary={
            <Box>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {getStudentEmail(session)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Session: {format(new Date(session.startsAt), 'dd-MM-yyyy HH:mm')} • Instructor: {getInstructorName(session)}
              </Typography>
            </Box>
          }
        />
        <ListItemSecondaryAction>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="View & Edit Feedback">
              <IconButton
                size="small"
                onClick={() => handleViewDetails(session)}
                sx={{
                  color: 'primary.main',
                  '&:hover': { backgroundColor: 'primary.50' },
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </ListItemSecondaryAction>
      </ListItem>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Speaking Feedback Management"
        subtitle="Review completed speaking sessions and provide feedback"
        icon={<FeedbackIcon />}
        iconColor="primary.main"
      />

      <StatsCards stats={stats} columns={3} />

      <GroupedAccordionList
        groups={groupsArray}
        itemsKey="sessions"
        isGroupExpanded={isGroupExpanded}
        onToggleGroup={toggleGroup}
        renderItem={renderItem}
        itemLabel="student"
      />

      {/* Details & Feedback Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VideoCallIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Speaking Session Feedback
              </Typography>
            </Box>
            {!editMode && (
              <ModernButton startIcon={<EditIcon />} onClick={handleEditFeedback} variant="contained" size="small">
                Edit Feedback
              </ModernButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedSession && (
            <Box>
              {/* Student & Session Info */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Student Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {getStudentName(selectedSession)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Email
                  </Typography>
                  <Typography variant="body1">
                    {getStudentEmail(selectedSession)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Session Date & Time
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(selectedSession.startsAt), 'dd-MM-yyyy HH:mm')} - {format(new Date(selectedSession.endsAt), 'HH:mm')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Instructor
                  </Typography>
                  <Typography variant="body1">
                    {getInstructorName(selectedSession)}
                  </Typography>
                </Grid>
                {selectedSession.meetLink && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                      Meeting Link
                    </Typography>
                    <Link href={selectedSession.meetLink} target="_blank" rel="noopener noreferrer">
                      {selectedSession.meetLink}
                    </Link>
                  </Grid>
                )}
              </Grid>

              {/* Feedback Section */}
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, borderBottom: 1, borderColor: 'divider', pb: 1 }}>
                Instructor Feedback
              </Typography>

              {editMode ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 600 }}>
                    Feedback
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={editedFeedback.instructorFeedback}
                    onChange={(e) => handleFeedbackChange('instructorFeedback', e.target.value)}
                    placeholder="Provide detailed feedback on the student's speaking performance..."
                    sx={{ mb: 3 }}
                  />

                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 600 }}>
                    Advice for Improvement
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={editedFeedback.instructorAdvice}
                    onChange={(e) => handleFeedbackChange('instructorAdvice', e.target.value)}
                    placeholder="Provide suggestions and advice for improvement..."
                  />
                </Box>
              ) : (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 600 }}>
                    Feedback
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: 'info.50', mb: 3, minHeight: 80 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedSession.instructorFeedback || 'No feedback provided yet'}
                    </Typography>
                  </Paper>

                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 600 }}>
                    Advice for Improvement
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: 'success.50', minHeight: 60 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedSession.instructorAdvice || 'No advice provided yet'}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {editMode ? (
            <>
              <ModernButton onClick={() => setEditMode(false)} variant="outlined" startIcon={<CloseIcon />}>
                Cancel
              </ModernButton>
              <ModernButton onClick={handleSaveFeedback} variant="contained" startIcon={<SaveIcon />}>
                Save Feedback
              </ModernButton>
            </>
          ) : (
            <ModernButton onClick={() => setDialogOpen(false)} variant="outlined">
              Close
            </ModernButton>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SpeakingFeedbackList;
